import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Inbox, CheckCircle2, XCircle, Clock, User, Mail, Phone, Building2, Copy, Sparkles, ExternalLink, MessageSquare, Send, RefreshCw, Eye, EyeOff, Pencil, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function fmt(iso) { try { return new Date(iso).toLocaleString(); } catch { return iso; } }
function money(n) { try { return `₹${Number(n || 0).toLocaleString()}`; } catch { return n; } }

// Small copyable pill — one click copies + toasts.
function CopyPill({ label, value, mono = true, secret = false, testId }) {
  const [shown, setShown] = useState(!secret);
  const copy = async () => {
    if (!value) { toast.error(`No ${label} to copy`); return; }
    try { await navigator.clipboard.writeText(String(value)); toast.success(`${label} copied`); }
    catch { toast.error("Copy failed — please copy manually"); }
  };
  const display = secret && !shown ? "•".repeat(Math.min(12, String(value || "").length || 8)) : (value || "—");
  return (
    <div className="inline-flex items-center gap-1 rounded-md bg-muted/40 border border-border px-2 py-1 text-[11px] leading-none max-w-full" data-testid={testId}>
      <span className="text-muted-foreground uppercase tracking-widest text-[9px] font-bold flex-shrink-0">{label}</span>
      <span className={`${mono ? "font-mono" : ""} text-foreground truncate max-w-[16ch] sm:max-w-[26ch]`} title={value || ""}>{display}</span>
      {secret && (
        <button onClick={() => setShown((v) => !v)} className="text-muted-foreground hover:text-foreground" title={shown ? "Hide" : "Show"} data-testid={`${testId}-toggle`}>
          {shown ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
        </button>
      )}
      <button onClick={copy} className="text-muted-foreground hover:text-foreground" title={`Copy ${label}`} data-testid={`${testId}-copy`}>
        <Copy className="w-3 h-3" />
      </button>
    </div>
  );
}

export default function PendingRegistrations() {
  const [rows, setRows] = useState([]);
  const [deletedRows, setDeletedRows] = useState([]);
  const [welcomeIndex, setWelcomeIndex] = useState({}); // registration_id → welcome_message
  const [tab, setTab] = useState("PENDING_APPROVAL");
  const [detail, setDetail] = useState(null);
  const [result, setResult] = useState(null);
  const [welcomeMsg, setWelcomeMsg] = useState(null);
  const [resending, setResending] = useState(false);
  const [busyRow, setBusyRow] = useState(null); // registration_id being approved/rejected
  const [editRow, setEditRow] = useState(null);  // registration being edited
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [drilldown, setDrilldown] = useState(null); // {tenant_id} → users drilldown modal

  const load = async () => {
    try {
      if (tab === "DELETED") {
        const { data } = await api.get("/admin/deleted-registrations");
        setDeletedRows(data);
        setRows([]);
        return;
      }
      const { data } = await api.get(`/admin/pending-registrations${tab === "ALL" ? "" : `?status=${tab}`}`);
      setRows(data);
      // Also fetch every welcome message so we can inline email/password/URL on APPROVED rows.
      const { data: msgs } = await api.get("/admin/welcome-messages");
      const idx = {};
      (msgs || []).forEach((m) => { if (m.registration_id) idx[m.registration_id] = m; });
      setWelcomeIndex(idx);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const approve = async (id) => {
    setBusyRow(id);
    try {
      const { data } = await api.post(`/admin/pending-registrations/${id}/approve`);
      let msg = null;
      try {
        const { data: msgs } = await api.get(`/admin/welcome-messages?registration_id=${id}`);
        msg = msgs?.[0] || null;
      } catch { /* soft-fail */ }
      setResult({ ...data, welcome_message: msg });
      const deliv = data.email_delivery;
      if (deliv?.sent) toast.success(`Approved · email delivered via ${(deliv.provider || "smtp").toUpperCase()}`);
      else toast.success("Approved · welcome message queued (SMTP not yet configured)");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Approval failed — please try again"); }
    finally { setBusyRow(null); }
  };
  const reject = async (id) => {
    const reason = window.prompt("Reason for rejection?");
    if (reason === null) { toast("Rejection cancelled"); return; }
    setBusyRow(id);
    try { await api.post(`/admin/pending-registrations/${id}/reject`, { reason }); toast.success("Registration rejected"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Reject failed"); }
    finally { setBusyRow(null); }
  };

  const openEdit = (r) => {
    setEditRow(r);
    setEditForm({
      company_name: r.company_name || "",
      contact_name: r.contact_name || "",
      email: r.email || "",
      phone: r.phone || "",
      address: r.address || "",
      gstin: r.gstin || "",
      message: r.message || "",
    });
  };

  const saveEdit = async () => {
    if (!editRow) return;
    setSavingEdit(true);
    try {
      await api.patch(`/admin/pending-registrations/${editRow.id}`, editForm);
      toast.success("Registration updated");
      setEditRow(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally { setSavingEdit(false); }
  };

  const remove = async (r) => {
    const label = `Delete registration for ${r.email}?\n\nThis will PERMANENTLY remove the tenant, all its users, welcome messages and login history. A snapshot is kept in the Deleted tab.\n\nThis cannot be undone.`;
    if (!window.confirm(label)) { toast("Deletion cancelled"); return; }
    setBusyRow(r.id);
    try {
      const { data } = await api.delete(`/admin/pending-registrations/${r.id}`);
      const c = data?.cascade_counts || {};
      toast.success(`Deleted — purged ${c.users || 0} user(s), ${c.tenants || 0} tenant`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally { setBusyRow(null); }
  };

  const viewWelcome = async (r) => {
    if (!r.provisioned_tenant_id) { toast.error("This registration hasn't been approved yet."); return; }
    try {
      const { data: msgs } = await api.get(`/admin/welcome-messages?registration_id=${r.id}`);
      if (msgs?.[0]) { setWelcomeMsg(msgs[0]); }
      else toast.error("Welcome message not found in queue");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load welcome message"); }
  };

  const resend = async () => {
    if (!welcomeMsg) return;
    setResending(true);
    try {
      const { data } = await api.post(`/admin/welcome-messages/${welcomeMsg.id}/resend`);
      if (data.delivery?.sent) toast.success(`Email delivered via ${(data.delivery.provider || "smtp").toUpperCase()}`);
      else if (data.delivery?.error) toast.error(`SMTP error: ${String(data.delivery.error).slice(0, 120)}`);
      else toast.success("Re-queued · configure SMTP to actually dispatch");
      // Refresh
      try {
        const { data: fresh } = await api.get(`/admin/welcome-messages?registration_id=${welcomeMsg.registration_id}`);
        if (fresh?.[0]) setWelcomeMsg(fresh[0]);
      } catch { /* ignore */ }
    } catch (e) { toast.error(e.response?.data?.detail || "Resend failed"); }
    finally { setResending(false); }
  };

  const openUrl = (url) => {
    if (!url) { toast.error("No URL available yet"); return; }
    window.open(url, "_blank", "noopener,noreferrer");
  };
  const copyText = async (label, value) => {
    if (!value) { toast.error(`No ${label} to copy`); return; }
    try { await navigator.clipboard.writeText(String(value)); toast.success(`${label} copied`); }
    catch { toast.error("Copy failed"); }
  };

  const badge = (r) => {
    const map = {
      PENDING_APPROVAL: { c: "bg-orange-500/20 text-orange-700 border-orange-500/40", i: Clock },
      PENDING_PAYMENT: { c: "bg-amber-500/20 text-amber-700 border-amber-500/40", i: Clock },
      APPROVED: { c: "bg-emerald-500/20 text-emerald-700 border-emerald-500/40", i: CheckCircle2 },
      REJECTED: { c: "bg-red-500/20 text-red-700 border-red-500/40", i: XCircle },
    };
    const m = map[r.status] || map.PENDING_APPROVAL;
    const Icon = m.i;
    return <Badge variant="outline" className={`${m.c} font-mono text-[10px]`}><Icon className="w-3 h-3 mr-1" /> {r.status}</Badge>;
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="admin-pending-regs">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-black flex items-center gap-2"><Inbox className="w-7 h-7" /> Registration inbox</h1>
        <p className="text-muted-foreground">Every public registration lands here. Free-trial requests arrive instantly (with the requested trial duration). Paid subscriptions arrive after payment is confirmed.</p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-2 flex-wrap">
          {["PENDING_APPROVAL", "PENDING_PAYMENT", "APPROVED", "REJECTED", "DELETED", "ALL"].map((s) => (
            <Button key={s} variant={tab === s ? "default" : "outline"} size="sm" onClick={() => setTab(s)} data-testid={`inbox-tab-${s}`}>{s.replace("_", " ")}</Button>
          ))}
        </div>
        <Button size="sm" variant="outline" onClick={async () => {
          try {
            const { data } = await api.post("/admin/welcome-messages/backfill-urls");
            toast.success(`Repaired ${data.updated}/${data.scanned} legacy welcome URLs`);
            load();
          } catch (e) { toast.error(e.response?.data?.detail || "Backfill failed"); }
        }} data-testid="backfill-urls-btn"><RefreshCw className="w-3 h-3 mr-1" /> Repair legacy URLs</Button>
      </div>

      <Card className="p-0 overflow-x-auto">
        {tab === "DELETED" ? (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Company / Admin</TableHead><TableHead>Portal / Plan</TableHead>
              <TableHead>Deleted by</TableHead><TableHead>Users purged</TableHead>
              <TableHead>Snapshot</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {deletedRows.map((r) => (
                <TableRow key={r.id} data-testid={`del-row-${r.id}`}>
                  <TableCell>
                    <div className="font-semibold">{r.company_name || "—"}</div>
                    <div className="text-[10px] text-muted-foreground">{r.contact_name} · {r.email}</div>
                    <div className="text-[10px] text-muted-foreground">Tenant: <span className="font-mono">{r.tenant_slug || "—"}</span></div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="font-mono text-[10px]">{r.portal}</Badge>
                    <div className="text-[10px] mt-1 font-mono">{r.plan_code}</div>
                    <div className="text-[10px] text-muted-foreground">{r.plan_name}</div>
                  </TableCell>
                  <TableCell>
                    <div className="text-xs font-mono">{r.deleted_by}</div>
                    <div className="text-[10px] text-muted-foreground">{fmt(r.deleted_at)}</div>
                    <Badge variant="outline" className="mt-1 text-[9px]">Was: {r.status_at_deletion}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm font-bold">{r.user_count || 0}</div>
                    <div className="text-[10px] text-muted-foreground max-w-[240px] truncate" title={(r.user_emails || []).join(", ")}>
                      {(r.user_emails || []).slice(0, 3).join(", ")}
                      {(r.user_emails || []).length > 3 ? ` +${r.user_emails.length - 3} more` : ""}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="grid grid-cols-2 gap-1 text-[10px] font-mono">
                      {Object.entries(r.cascade_counts || {}).map(([k, v]) => (
                        <div key={k} className="text-muted-foreground">{k}: <span className="text-foreground font-bold">{v}</span></div>
                      ))}
                    </div>
                    {r.custom_url && (
                      <div className="text-[10px] text-muted-foreground mt-1 truncate max-w-[220px]" title={r.custom_url}>URL: {r.custom_url}</div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {deletedRows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-10">No deleted registrations.</TableCell></TableRow>}
            </TableBody>
          </Table>
        ) : (
        <Table>
          <TableHeader><TableRow>
            <TableHead>Company</TableHead><TableHead>Portal</TableHead><TableHead>Plan</TableHead>
            <TableHead>Trial / Amount</TableHead><TableHead>Status</TableHead>
            <TableHead>Access</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => {
              const wm = welcomeIndex[r.id];
              const loginUrl = r.custom_url || wm?.custom_url;
              const loginEmail = wm?.channel_email?.to || r.email;
              const tempPass = wm?.temp_password;
              return (
                <TableRow key={r.id} data-testid={`reg-row-${r.id}`}>
                  <TableCell>
                    <div className="font-semibold">{r.company_name}</div>
                    <div className="text-[10px] text-muted-foreground">{r.contact_name} · {r.email}</div>
                    <div className="text-[10px] text-muted-foreground">{fmt(r.created_at)}</div>
                    {r.status === "APPROVED" && (r.provisioned_tenant_id || r.tenant_id) && (
                      <button
                        onClick={() => setDrilldown({ tenant_id: r.provisioned_tenant_id || r.tenant_id, company_name: r.company_name })}
                        className="mt-1 inline-flex items-center gap-1 text-[10px] text-orange-600 hover:text-orange-700 font-semibold"
                        data-testid={`reg-drilldown-${r.id}`}
                      >
                        <User className="w-3 h-3" /> View team &amp; activity →
                      </button>
                    )}
                  </TableCell>
                  <TableCell><Badge variant="outline" className="font-mono text-[10px]">{r.portal}</Badge></TableCell>
                  <TableCell><div className="font-mono text-[11px]">{r.plan_code}</div><div className="text-[10px] text-muted-foreground">{r.plan_name}</div></TableCell>
                  <TableCell>
                    {r.is_trial
                      ? <div className="flex items-center gap-1 text-sm"><Sparkles className="w-3 h-3 text-orange-500" /> {r.trial_days} days<div className="text-[10px] text-muted-foreground">{r.trial_end ? `ends ${(r.trial_end || "").slice(0,10)}` : ""}</div></div>
                      : <div className="font-mono font-bold">{money(r.amount_inr)}</div>}
                    <Badge variant="outline" className="font-mono text-[9px] mt-1">{r.payment_status || "—"}</Badge>
                  </TableCell>
                  <TableCell>{badge(r)}</TableCell>
                  <TableCell>
                    {r.status === "APPROVED" && loginUrl ? (
                      <div className="flex flex-col gap-1 items-start" data-testid={`access-cell-${r.id}`}>
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => openUrl(loginUrl)} title="Open login URL in new tab" data-testid={`open-url-${r.id}`}>
                            <ExternalLink className="w-3 h-3 mr-1" /> Open
                          </Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => copyText("Login URL", loginUrl)} title="Copy login URL" data-testid={`copy-url-${r.id}`}>
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                        <CopyPill label="Email" value={loginEmail} testId={`access-email-${r.id}`} />
                        {tempPass && <CopyPill label="Password" value={tempPass} secret testId={`access-password-${r.id}`} />}
                      </div>
                    ) : (
                      <span className="text-[11px] text-muted-foreground italic">Approve to generate credentials</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button size="sm" variant="ghost" onClick={() => setDetail(r)} data-testid={`reg-view-${r.id}`}>View</Button>
                    {r.status === "PENDING_APPROVAL" && (
                      <>
                        <Button size="sm" onClick={() => approve(r.id)} disabled={busyRow === r.id} data-testid={`reg-approve-${r.id}`}>
                          {busyRow === r.id ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : null}
                          Approve
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => reject(r.id)} disabled={busyRow === r.id} data-testid={`reg-reject-${r.id}`}>Reject</Button>
                      </>
                    )}
                    {r.status === "APPROVED" && (
                      <Button size="sm" variant="outline" onClick={() => viewWelcome(r)} data-testid={`reg-msg-${r.id}`}>
                        <MessageSquare className="w-3 h-3 mr-1" /> Welcome msg
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(r)} title="Edit contact details" data-testid={`reg-edit-${r.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => remove(r)} disabled={busyRow === r.id} title="Delete registration row" data-testid={`reg-delete-${r.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">Nothing here yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
        )}
      </Card>

      {/* Detail modal */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col p-0" data-testid="reg-detail-modal">
          <DialogHeader className="px-6 pt-6 pb-2 flex-shrink-0">
            <DialogTitle>{detail?.company_name}</DialogTitle>
            <DialogDescription>Full registration record + provisioned URL if approved.</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-2">
            {detail && (
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2"><Building2 className="w-4 h-4 text-muted-foreground" /> <b>{detail.portal}</b> · plan <b>{detail.plan_code}</b> · {detail.billing_cycle}</div>
                <div className="flex items-center gap-2"><User className="w-4 h-4 text-muted-foreground" /> {detail.contact_name}</div>
                <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-muted-foreground" /> {detail.email}</div>
                {detail.phone && <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-muted-foreground" /> {detail.phone}</div>}
                {detail.gstin && <div className="text-xs">GSTIN: <span className="font-mono">{detail.gstin}</span></div>}
                {detail.address && <div className="text-xs">Address: {detail.address}</div>}
                <div className="mt-2 pt-2 border-t">
                  {detail.is_trial
                    ? <div className="text-sm"><b>Free trial</b> — {detail.trial_days} days {detail.trial_end ? `(ends ${(detail.trial_end || "").slice(0,10)})` : ""}</div>
                    : <div className="text-sm"><b>Amount:</b> <span className="font-mono">{money(detail.amount_inr)}</span> · payment <b>{detail.payment_status}</b></div>}
                </div>
                {detail.message && <div className="mt-2 text-xs italic text-muted-foreground">&ldquo;{detail.message}&rdquo;</div>}
                {detail.custom_url && (
                  <div className="mt-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-3 text-xs">
                    <div className="font-semibold">Provisioned URL</div>
                    <div className="font-mono break-all">{detail.custom_url}</div>
                    <div className="flex gap-2 mt-2">
                      <Button size="sm" variant="outline" onClick={() => copyText("Login URL", detail.custom_url)}><Copy className="w-3 h-3 mr-1" /> Copy</Button>
                      <Button size="sm" variant="outline" onClick={() => openUrl(detail.custom_url)}><ExternalLink className="w-3 h-3 mr-1" /> Open</Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="px-6 py-4 border-t flex-shrink-0"><Button variant="outline" onClick={() => setDetail(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approval result modal */}
      <Dialog open={!!result} onOpenChange={(o) => !o && setResult(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col p-0" data-testid="approval-result-modal">
          <DialogHeader className="px-6 pt-6 pb-2 flex-shrink-0">
            <DialogTitle className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-500" /> Approved & provisioned</DialogTitle>
            <DialogDescription>Tenant + admin user are live. Share the login URL and temporary password with the applicant.</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-2">
            {result && (
              <div className="space-y-3 text-sm">
                <div>New tenant <b>{result.tenant_slug}</b> is live. Welcome message queued to <b>{result.user_email}</b>.</div>
                <div className="rounded-lg bg-muted/40 p-3 space-y-2">
                  <div className="text-xs font-semibold">Custom URL</div>
                  <div className="font-mono text-xs break-all">{result.custom_url}</div>
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => copyText("Login URL", result.custom_url)} data-testid="approve-copy-url"><Copy className="w-3 h-3 mr-1" /> Copy URL</Button>
                    <Button size="sm" onClick={() => openUrl(result.custom_url)} data-testid="approve-open-url"><ExternalLink className="w-3 h-3 mr-1" /> Open in new tab</Button>
                  </div>
                </div>
                <div className="rounded-lg bg-muted/40 p-3 space-y-2">
                  <div className="text-xs font-semibold">Login credentials</div>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="w-16 text-muted-foreground uppercase tracking-widest text-[9px] font-bold">Email</span>
                    <span className="font-mono flex-1 truncate">{result.user_email}</span>
                    <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => copyText("Email", result.user_email)} data-testid="approve-copy-email"><Copy className="w-3 h-3" /></Button>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="w-16 text-muted-foreground uppercase tracking-widest text-[9px] font-bold">Password</span>
                    <span className="font-mono font-bold flex-1 truncate">{result.temp_password}</span>
                    <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => copyText("Password", result.temp_password)} data-testid="approve-copy-password"><Copy className="w-3 h-3" /></Button>
                  </div>
                  <div className="text-[10px] text-muted-foreground italic">User will be forced to change on first login.</div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="px-6 py-4 border-t flex-shrink-0"><Button onClick={() => setResult(null)}>Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Welcome message viewer (for already-approved regs) */}
      <Dialog open={!!welcomeMsg} onOpenChange={(o) => !o && setWelcomeMsg(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0" data-testid="welcome-msg-modal">
          <DialogHeader className="px-6 pt-6 pb-2 flex-shrink-0">
            <DialogTitle className="flex items-center gap-2"><MessageSquare className="w-5 h-5 text-orange-500" /> Welcome message</DialogTitle>
            <DialogDescription>Preview the branded HTML email. Copy URL / credentials or trigger a re-send.</DialogDescription>
          </DialogHeader>
          {welcomeMsg && (
            <>
              {/* Sticky quick-access ribbon so URL + creds + Send button are ALWAYS visible */}
              <div className="px-6 py-3 border-b bg-muted/30 flex-shrink-0 flex flex-wrap items-center gap-2" data-testid="welcome-msg-ribbon">
                <Button size="sm" onClick={() => openUrl(welcomeMsg.custom_url)} data-testid="welcome-open-url"><ExternalLink className="w-3 h-3 mr-1" /> Open login URL</Button>
                <CopyPill label="Email" value={welcomeMsg.channel_email?.to} testId="welcome-email-pill" />
                <CopyPill label="Password" value={welcomeMsg.temp_password} secret testId="welcome-password-pill" />
                <div className="ml-auto flex gap-2">
                  <Button size="sm" variant="outline" onClick={resend} disabled={resending} data-testid="welcome-msg-resend">
                    <RefreshCw className={`w-3 h-3 mr-1 ${resending ? "animate-spin" : ""}`} /> Send / Resend
                  </Button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3 text-sm">
                <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                  <span>To: <b>{welcomeMsg.channel_email?.to}</b></span>
                  {welcomeMsg.resent_at && <span>· Resent {welcomeMsg.resend_count}×</span>}
                  {welcomeMsg.delivery?.provider && (
                    <Badge variant="outline" className={welcomeMsg.delivery?.sent ? "border-emerald-400 bg-emerald-500/10 text-emerald-700" : "border-amber-400 bg-amber-500/10 text-amber-700"}>
                      {welcomeMsg.delivery?.sent ? `Sent via ${welcomeMsg.delivery.provider.toUpperCase()}` : `Queued (${welcomeMsg.delivery?.provider || "mock"})`}
                    </Badge>
                  )}
                </div>
                <div className="rounded-lg bg-muted/40 p-3">
                  <div className="text-xs font-semibold mb-1">Subject</div>
                  <div className="text-sm">{welcomeMsg.channel_email?.subject}</div>
                </div>
                {welcomeMsg.channel_email?.html ? (
                  <div className="rounded-lg border overflow-hidden bg-white" data-testid="welcome-msg-html">
                    <div className="text-[10px] uppercase tracking-widest bg-slate-100 px-3 py-1 text-slate-500 font-bold border-b">Branded HTML preview</div>
                    <iframe title="welcome-html" srcDoc={welcomeMsg.channel_email.html} className="w-full" style={{ height: 480, border: 0 }} />
                  </div>
                ) : (
                  <pre className="text-[11px] font-mono whitespace-pre-wrap max-h-64 overflow-y-auto bg-muted/40 p-3 rounded border" data-testid="welcome-msg-body">{welcomeMsg.channel_email?.body}</pre>
                )}
                <div className="flex gap-2 flex-wrap pt-1">
                  <Button size="sm" variant="outline" onClick={() => copyText("Plain-text body", welcomeMsg.channel_email?.body)}><Copy className="w-3 h-3 mr-1" /> Copy plaintext</Button>
                  {welcomeMsg.channel_email?.html && (
                    <Button size="sm" variant="outline" onClick={() => copyText("HTML body", welcomeMsg.channel_email?.html)} data-testid="welcome-copy-html"><Copy className="w-3 h-3 mr-1" /> Copy HTML</Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => {
                    const to = welcomeMsg.channel_email?.to;
                    if (!to) { toast.error("No recipient email"); return; }
                    window.location.href = `mailto:${to}?subject=${encodeURIComponent(welcomeMsg.channel_email?.subject || "")}&body=${encodeURIComponent(welcomeMsg.channel_email?.body || "")}`;
                    toast.success("Opening default mail app…");
                  }}>
                    <Send className="w-3 h-3 mr-1" /> Send via mail app
                  </Button>
                  {welcomeMsg.channel_whatsapp?.to && (
                    <Button size="sm" variant="outline" onClick={() => {
                      window.open(`https://wa.me/${String(welcomeMsg.channel_whatsapp.to).replace(/\D/g, "")}?text=${encodeURIComponent(welcomeMsg.channel_email?.body || "")}`, "_blank", "noopener,noreferrer");
                      toast.success("Opening WhatsApp…");
                    }}>
                      <MessageSquare className="w-3 h-3 mr-1" /> WhatsApp
                    </Button>
                  )}
                </div>
              </div>
              <DialogFooter className="px-6 py-3 border-t flex-shrink-0"><Button onClick={() => setWelcomeMsg(null)}>Close</Button></DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit modal — lets Super Admin fix a mistyped contact name / email / phone on a registration */}
      <Dialog open={!!editRow} onOpenChange={(o) => !o && setEditRow(null)}>
        <DialogContent className="max-w-lg" data-testid="reg-edit-modal">
          <DialogHeader>
            <DialogTitle>Edit registration</DialogTitle>
            <DialogDescription>
              Update the contact details captured at signup. Changes to <strong>email</strong> do NOT rotate the tenant login — use the Org Users page for that.
            </DialogDescription>
          </DialogHeader>
          {editRow && (
            <div className="space-y-3 py-2">
              <div className="space-y-1"><Label className="text-xs">Company name</Label>
                <Input value={editForm.company_name} onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })} data-testid="reg-edit-company" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Contact name</Label>
                  <Input value={editForm.contact_name} onChange={(e) => setEditForm({ ...editForm, contact_name: e.target.value })} data-testid="reg-edit-contact" />
                </div>
                <div className="space-y-1"><Label className="text-xs">Phone</Label>
                  <Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} data-testid="reg-edit-phone" />
                </div>
              </div>
              <div className="space-y-1"><Label className="text-xs">Email</Label>
                <Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} data-testid="reg-edit-email" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">GSTIN</Label>
                  <Input value={editForm.gstin} onChange={(e) => setEditForm({ ...editForm, gstin: e.target.value })} data-testid="reg-edit-gstin" />
                </div>
                <div className="space-y-1"><Label className="text-xs">Address</Label>
                  <Input value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} data-testid="reg-edit-address" />
                </div>
              </div>
              <div className="space-y-1"><Label className="text-xs">Note / message</Label>
                <Input value={editForm.message} onChange={(e) => setEditForm({ ...editForm, message: e.target.value })} data-testid="reg-edit-message" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditRow(null)} data-testid="reg-edit-cancel">Cancel</Button>
            <Button onClick={saveEdit} disabled={savingEdit} data-testid="reg-edit-save">
              {savingEdit ? <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Saving</> : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tenant users drilldown — SA sees the full team + login activity per user */}
      <TenantDrilldownModal payload={drilldown} onClose={() => setDrilldown(null)} />
    </div>
  );
}

function TenantDrilldownModal({ payload, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!payload?.tenant_id) { setData(null); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: d } = await api.get(`/admin/tenants/${payload.tenant_id}/users`);
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || "Failed to load team");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [payload?.tenant_id]);

  const grouped = data?.grouped_by_role || {};
  const roleOrder = Object.keys(grouped).sort((a, b) => {
    // Admin-ish roles first
    const rank = (r) => r.endsWith("_ADMIN") ? 0 : r.includes("MANAGER") ? 1 : 2;
    return rank(a) - rank(b) || a.localeCompare(b);
  });

  const fmt = (iso) => iso ? new Date(iso).toLocaleString() : "—";

  return (
    <Dialog open={!!payload?.tenant_id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0" data-testid="tenant-drilldown-modal">
        <DialogHeader className="px-6 pt-6 pb-3 border-b flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <User className="w-4 h-4 text-orange-500" /> Team &amp; activity — {payload?.company_name || data?.tenant?.name || "…"}
          </DialogTitle>
          <DialogDescription>
            Every user this tenant has provisioned, grouped by role, with their most recent login and audit event.
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
          {!loading && data && (
            <>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="rounded-lg border border-border p-3 bg-orange-50/40">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Total users</div>
                  <div className="text-2xl font-black font-display">{data.total_users}</div>
                </div>
                <div className="rounded-lg border border-border p-3">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Roles</div>
                  <div className="text-2xl font-black font-display">{roleOrder.length}</div>
                </div>
                <div className="rounded-lg border border-border p-3">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Tenant</div>
                  <div className="text-xs font-mono truncate" title={data.tenant?.slug}>{data.tenant?.slug}</div>
                  <div className="text-[10px] text-muted-foreground">{data.tenant?.kind} · {data.tenant?.status}</div>
                </div>
              </div>

              {roleOrder.length === 0 && <div className="text-sm text-muted-foreground">No users provisioned yet.</div>}

              {roleOrder.map((role) => (
                <div key={role} className="mb-5" data-testid={`drilldown-role-${role}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="outline" className="font-mono text-[10px]">{role}</Badge>
                    <div className="text-xs text-muted-foreground">{grouped[role].length} user{grouped[role].length !== 1 ? "s" : ""}</div>
                  </div>
                  <div className="rounded-lg border border-border overflow-hidden">
                    <Table>
                      <TableHeader><TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Last login</TableHead>
                        <TableHead>Failed attempts</TableHead>
                        <TableHead>Latest audit</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {grouped[role].map((u) => (
                          <TableRow key={u.id} data-testid={`drilldown-user-${u.id || u.email}`}>
                            <TableCell>
                              <div className="text-sm font-semibold">{u.full_name || "—"}</div>
                              <div className="text-[11px] text-muted-foreground">{u.email}</div>
                              {u.must_change_password && <Badge variant="outline" className="mt-1 text-[9px]">Must change PW</Badge>}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className={`text-[10px] ${u.account_status === "ACTIVE" ? "border-emerald-500 text-emerald-700" : "border-amber-500 text-amber-700"}`}>
                                {u.account_status || "ACTIVE"}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="text-[11px]">{fmt(u.recent_logins?.[0]?.at)}</div>
                              {u.recent_logins?.[0]?.ip && <div className="text-[9px] text-muted-foreground font-mono">{u.recent_logins[0].ip}</div>}
                            </TableCell>
                            <TableCell>
                              {u.last_login_attempt?.count > 0 ? (
                                <Badge variant="outline" className="text-[10px] border-red-500 text-red-700">{u.last_login_attempt.count}</Badge>
                              ) : (
                                <span className="text-[10px] text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="text-[11px] font-mono">{u.last_audit_action || "—"}</div>
                              <div className="text-[9px] text-muted-foreground">{fmt(u.last_audit_at)}</div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
        <DialogFooter className="px-6 py-3 border-t flex-shrink-0">
          <Button variant="outline" onClick={onClose} data-testid="drilldown-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
